import React, { useState } from 'react';
import { Button } from './Button.tsx';
import { ShieldCheck, Zap, ArrowRight, CheckCircle, Database, Search, Target } from 'lucide-react';
import { motion } from 'framer-motion';

interface HeroProps {
  onStart: () => void;
  onNavigate: (section: string) => void;
}

export const Hero: React.FC<HeroProps> = ({ onStart, onNavigate }) => {
  return (
    <div className="relative min-h-[90vh] flex flex-col items-center justify-center pt-20 overflow-hidden">
      
      {/* Cinematic Background Elements */}
      <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/10 rounded-full blur-[120px] animate-pulse"></div>
          <div className="absolute top-0 w-full h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 flex flex-col items-center text-center">
        
        <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-3 px-6 py-2 rounded-full glass-card border-primary/20 text-primary text-[10px] font-mono font-black uppercase tracking-[0.3em] mb-12"
        >
          <div className="w-1.5 h-1.5 rounded-full bg-primary animate-ping"></div>
          System Status: Operational
        </motion.div>
        
        <motion.h1 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="text-6xl md:text-9xl font-black tracking-tighter leading-none mb-10 text-white"
        >
          KNOW BEFORE<br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-secondary to-primary animate-gradient-x">YOU SUBMIT</span>
        </motion.h1>
        
        <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="max-w-2xl text-lg text-subtext font-medium leading-relaxed mb-14 px-4"
        >
          SafeToSubmit integrates elite <span className="text-primary font-bold">forensic linguistics</span> with multi-pass <span className="text-secondary font-bold">LLM-signature neutralization</span>. The definitive solution for authentic writing.
        </motion.p>

        <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex flex-col sm:flex-row gap-6 mb-24"
        >
          <Button onClick={onStart} variant="primary" className="h-16 px-12 text-sm">
            Launch Terminal <ArrowRight className="ml-3 w-5 h-5" />
          </Button>
          <Button variant="outline" className="h-16 px-12 text-sm border-white/10 hover:border-white/20 text-white">
            View Forensic Docs
          </Button>
        </motion.div>

        {/* Tactical Feature Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full">
          {[
            { 
              title: "SafeScan Forensic", 
              desc: "Deep neural pattern recognition identifying structural AI fingerprints across all major models.", 
              icon: <Search className="text-primary w-6 h-6" />,
              label: "01_DETECTION"
            },
            { 
              title: "Stealth Bypass", 
              desc: "4-Pass linguistic refactoring designed to defeat GPTZero, Turnitin, and Originality.ai.", 
              icon: <Target className="text-secondary w-6 h-6" />,
              label: "02_RESTRUCTURING"
            },
            { 
              title: "Data Sovereignty", 
              desc: "Zero-knowledge encryption. Your data is scanned, humanized, and immediately purged.", 
              icon: <Database className="text-cyan-500 w-6 h-6" />,
              label: "03_SECURITY"
            }
          ].map((feature, i) => (
            <motion.div 
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 + (i * 0.1) }}
              className="glass-card p-10 text-left relative group hover:bg-white/5 transition-all cursor-default"
            >
              <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                <span className="text-4xl font-mono font-black">{i+1}</span>
              </div>
              <div className="text-[10px] font-mono font-black text-primary/40 uppercase tracking-[0.2em] mb-8">{feature.label}</div>
              <div className="w-14 h-14 rounded-none border border-white/5 flex items-center justify-center mb-8 group-hover:border-primary/40 transition-colors">
                {feature.icon}
              </div>
              <h3 className="text-lg font-bold text-white mb-4 uppercase tracking-widest">{feature.title}</h3>
              <p className="text-sm text-subtext leading-relaxed font-medium">{feature.desc}</p>
              
              {/* Tactical Corner Decoration */}
              <div className="absolute bottom-0 right-0 w-8 h-8 opacity-20 border-r border-b border-primary/50 translate-x-1 translate-y-1"></div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};